import OlMap from 'ol/Map';
import Feature, { FeatureLike } from 'ol/Feature';
import Circle from 'ol/geom/Circle';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import WKT from 'ol/format/WKT';
import { Fill, Stroke, Style, Text } from 'ol/style';
import { ZoneOfInterestDto } from 'src/app/bo/zoneOfInterest';
import { FEATURE_KIND, FEATURE_LABEL, MAP_PROJECTION, Z_INDEX } from '../site-map.constants';

export class ZonesOfInterestLayer {
  private readonly source = new VectorSource<Feature>();
  private readonly layer = new VectorLayer({
    source: this.source,
    zIndex: Z_INDEX.zonesOfInterest,
    style: feature => this.styleFor(feature),
  });
  private readonly wkt = new WKT();
  private readonly styleCache = new Map<string, Style>();
  private hovered: FeatureLike | null = null;

  constructor(private readonly map: OlMap) {
    map.addLayer(this.layer);
  }

  /** Les zones sont stockées en EPSG:3857 : un cercle (centre + rayon) ou un polygone WKT. */
  setZones(zones: ZoneOfInterestDto[]): void {
    this.hovered = null;
    this.source.clear();
    this.source.addFeatures(zones
      .map(zone => this.toFeature(zone))
      .filter((feature): feature is Feature => feature !== null));
  }

  setVisible(visible: boolean): void {
    this.layer.setVisible(visible);
  }

  setHovered(feature: FeatureLike | null): void {
    if (feature === this.hovered) return;
    this.hovered = feature;
    this.layer.changed();
  }

  dispose(): void {
    this.map.removeLayer(this.layer);
    this.source.clear();
    this.styleCache.clear();
  }

  private toFeature(zone: ZoneOfInterestDto): Feature | null {
    let feature: Feature | null = null;
    if (zone.geometryType === 'circle' && zone.centerX != null && zone.centerY != null && zone.radius != null) {
      feature = new Feature({ geometry: new Circle([zone.centerX, zone.centerY], zone.radius) });
    } else if (zone.geometryType === 'polygon' && zone.wktString) {
      try {
        feature = this.wkt.readFeature(zone.wktString, { dataProjection: MAP_PROJECTION, featureProjection: MAP_PROJECTION });
      } catch {
        feature = null;
      }
    }
    feature?.setProperties({ [FEATURE_KIND]: 'zone-of-interest', [FEATURE_LABEL]: zone.name ?? '' }, true);
    return feature;
  }

  private styleFor(feature: FeatureLike): Style {
    const name: string = feature.get(FEATURE_LABEL);
    const hovered = feature === this.hovered;
    const key = `${hovered}|${name}`;
    let style = this.styleCache.get(key);
    if (!style) {
      style = new Style({
        fill: new Fill({ color: hovered ? 'rgba(255, 165, 0, 0.4)' : 'rgba(255, 165, 0, 0.2)' }),
        stroke: hovered
          ? new Stroke({ color: 'rgba(255, 255, 255, 0.85)', width: 3 })
          : new Stroke({ color: 'rgba(255, 140, 0, 0.8)', width: 2, lineDash: [6, 4] }),
        text: new Text({
          font: hovered ? 'bold 13px Calibri,sans-serif' : '13px Calibri,sans-serif',
          text: name,
          fill: new Fill({ color: '#fff' }),
          backgroundFill: new Fill({ color: hovered ? 'rgba(255, 140, 0, 0.9)' : 'rgba(255, 140, 0, 0.7)' }),
          padding: [3, 5, 3, 5],
          overflow: true,
        }),
      });
      this.styleCache.set(key, style);
    }
    return style;
  }
}
